import { useState } from 'react';

import { Button, Icon } from '@efira/ui';

import { useOverlayToken } from '../api/queries';
import { Catalog } from './center/Catalog';
import { FrameLayout } from './center/FrameLayout';
import { useLayoutMode } from './center/layoutMode';
import type { LayoutMode } from './center/layoutMode';
import { useConsoleSession } from './session';

/**
 * Центр консоли: ОДНО И ТО ЖЕ МЕСТО в двух режимах.
 *
 *   сессии нет  - каталог игр;
 *   игра идёт   - кадр оверлея 9:16 с шахматкой прозрачности.
 *
 * Переход: нажатие на плитку запускает, «End Session» возвращает
 * (docs/reference/LIVECADE_UX.md, раздел 5). Это не два экрана и не две
 * вкладки: у эталона центр не перерисовывает окружение, меняется только он сам.
 */
export function CenterColumn() {
  const session = useConsoleSession();

  return (
    <div data-console-center className="flex h-full min-h-0 flex-col">
      <div
        data-console-scroll="center"
        tabIndex={0}
        role="region"
        aria-label="Оверлей и каталог"
        className="min-h-0 flex-1 overflow-y-auto"
      >
        {session.game === null ? <Catalog /> : <FrameMode />}
      </div>

      {/*
        ПОЛОСА ТОСТОВ ПОД КАТАЛОГОМ. Плитки каталога - органы управления, и
        тост поверх них закрывал бы игру, которую человек собирался выбрать.
        Полоса той же высоты, что держат окна (`--toast-lane`), пустая,
        пока тоста нет. В режиме кадра её нет: тост встаёт в нижнюю
        охранную зону самого кадра (`toastLane.ts`).
      */}
      {session.game === null && (
        <div data-toast-lane aria-hidden className="h-[var(--toast-lane)] shrink-0" />
      )}
    </div>
  );
}

/**
 * Режим кадра.
 *
 * Внутри кадра - НАСТОЯЩИЙ ОВЕРЛЕЙ, тот же, что видит зритель: отдельный
 * фрейм по адресу `/s/<токен>?preview=1`. Это самое ценное место консоли -
 * стример смотрит сюда и отсюда понимает, что игра работает.
 *
 * ПОЧЕМУ ФРЕЙМ, А НЕ ВСТРОЕННЫЙ КОД. Оверлей - отдельное приложение с
 * неприкосновенными воротами веса. Затащив его сборку в консоль, мы бы либо
 * раздули dashboard, либо начали подгонять оверлей под чужие нужды. Фрейм
 * оставляет оба приложения на своих местах и своих бюджетах.
 *
 * ПОДДОМЕНЫ ВСТРАИВАНИЮ НЕ МЕШАЮТ. Оверлей намеренно не ставит
 * `X-Frame-Options` и `frame-ancestors` (`apps/overlay/public/_headers`):
 * его встраивают в OBS и Streamlabs, и запрет сломал бы основной сценарий.
 * Cookie тут ни при чём - оверлей опознаётся токеном в адресе, а не сессией,
 * поэтому третьесторонние cookie не нужны.
 *
 * ПРИЗНАК `preview=1` - НЕ УКРАШЕНИЕ. Кадр консоли и оверлей в OBS
 * подключаются одинаково; без признака открытая консоль показывала бы
 * «оверлей подключён» всегда, и индикатор присутствия перестал бы значить
 * что-либо. Сервер считает такие подключения отдельно: мир они держат,
 * в присутствие не входят.
 */
function FrameMode() {
  const token = useOverlayToken();
  const session = useConsoleSession();
  const layout = useLayoutMode();
  const [reloadKey, setReloadKey] = useState(0);

  /* Адрес кадра. Собирается из боевой ссылки оверлея: где он живёт, знает
     сервер, а не консоль. */
  const src =
    token.data === undefined
      ? null
      : `${token.data.url}${token.data.url.includes('?') ? '&' : '?'}preview=1`;

  return (
    /*
     * КАДР ВО ВСЮ ВЫСОТУ КОЛОНКИ, ИНСТРУМЕНТЫ - ВЕРТИКАЛЬНО СБОКУ.
     * Решение владельца 2026-09-29 (2), находка 10 аудита.
     *
     * Было: над кадром строка под полосу режима раскладки, вокруг поля по
     * 20 px, и кадр 9:16 в центре шириной 650 px оставлял по ~200 px пустоты
     * с каждой стороны. Теперь строки над кадром нет, поля 12 px, а все
     * органы кадра - режим раскладки, перезагрузка и полоса режима - стоят
     * столбцом справа от него. Заводская доля центра ужата под кадр во всю
     * высоту, освободившееся место отдано боковым колонкам (`geometry.ts`).
     * Заводская раскладка самого оверлея не менялась.
     */
    <div data-console-mode="frame" className="flex h-full min-h-0 gap-2 p-3">
      {/*
        Контейнер запроса ПО ОБЕИМ ОСЯМ (`container-type: size`): кадр
        вписывается по меньшей стороне, и для этого ему нужно знать и высоту
        колонки, и её ширину.
      */}
      <div className="relative flex min-h-0 min-w-0 flex-1 items-center justify-center [container-type:size]">
        <div
          data-console-frame
          className="border-foreground/15 relative overflow-hidden rounded-lg border"
          style={{
            aspectRatio: '9 / 16',
            /*
              ВПИСЫВАНИЕ ПО МЕНЬШЕЙ СТОРОНЕ. Раньше стояло `h-full`, и высота
              колонки одна задавала размер: на узкой границе кадр шириной
              461 px оставался неизменным и вылезал за колонку - пропорция
              формально держалась, а видимая часть выглядела квадратной.
              Теперь высота - минимум из высоты колонки и того, что позволяет
              её ширина.
            */
            height: 'min(100cqh, calc(100cqw * 16 / 9))',
            /*
              Шахматку прозрачности рисует САМ ОВЕРЛЕЙ в режиме предпросмотра
              (`apps/overlay/src/main.css`, класс `preview`). Здесь она была
              бы не видна: Chromium подкладывает под встроенный документ свою
              непрозрачную основу, и подложка встраивающей страницы сквозь
              неё не проступает. Остаётся подложка на время загрузки оверлея.
            */
            backgroundColor: 'var(--color-border)',
          }}
        >
          {src === null ? (
            <p className="text-foreground/80 absolute inset-0 grid place-items-center text-sm">
              Оверлей загружается…
            </p>
          ) : (
            <>
              <iframe
                key={reloadKey}
                data-console-overlay-frame
                src={src}
                title="Оверлей"
                /* Ни скриптов сторонних доменов, ни форм: оверлей рисует
                   сцену и держит сокет. Разрешения выданы ровно под это. */
                allow="autoplay"
                className="absolute inset-0 size-full border-0"
              />

              {/*
                Режим раскладки - СЛОЙ ПОВЕРХ живого оверлея, а не замена
                ему: игра продолжает идти, и стример двигает элементы по
                тому, что видит зритель.
              */}
              {layout.editing && session.gameId !== null && (
                <FrameLayout gameId={session.gameId} onExit={layout.toggle} />
              )}
            </>
          )}
        </div>
      </div>

      {/*
        ПАНЕЛЬ ИНСТРУМЕНТОВ КАДРА - ОДИН СТОЛБЕЦ СПРАВА ОТ НЕГО.

        Вне режима: вход в режим раскладки и перезагрузка оверлея (browser
        source в OBS стример перезапускает кнопкой источника, а у оверлея
        консоли такой кнопки нет). В режиме: полоса режима раскладки
        приходит сюда же порталом (`data-frame-toolbar-slot`), а вход в
        режим уходит - второй «Готово» рядом с первым заставлял выбирать
        между ними.

        Столбец, а не строка над кадром: строка отнимала у кадра высоту, а
        кадр 9:16 упирается именно в неё.
      */}
      <div
        data-control-row="frame-actions"
        role="toolbar"
        aria-orientation="vertical"
        aria-label="Инструменты оверлея"
        className="flex shrink-0 flex-col items-center gap-1"
      >
        {!layout.editing && <LayoutModeButton layout={layout} />}
        <div data-frame-toolbar-slot className="contents" />
        <Button
          variant="ghost"
          size="s"
          iconOnly
          data-console-frame-reload
          aria-label="Перезагрузить оверлей"
          title="Перезагрузить оверлей"
          onClick={() => {
            setReloadKey((value) => value + 1);
          }}
        >
          <Icon name="arrowSync" size="sm" />
        </Button>
      </div>
    </div>
  );
}

/**
 * Кнопка режима раскладки - пункт реестра `console.center.hud-btn`.
 *
 * У эталона она стоит справа сверху от кадра и включает режим в нём же.
 * У нас так же; отличие в том, что редактор наш собственный и умеет больше
 * (охранные зоны, отмена, перенос между играми) - см. `layoutMode.ts`.
 *
 * Кнопка только ВХОДИТ в режим. Выход - «Готово» в полосе управления над
 * кадром: два выхода из одного режима ничего не добавляют, кроме выбора.
 */
function LayoutModeButton({ layout }: { layout: LayoutMode }) {
  return (
    <Button
      variant="ghost"
      size="s"
      iconOnly
      data-console-hud-btn
      aria-pressed={layout.editing}
      aria-label="Двигать части оверлея"
      title="Двигать части оверлея"
      onClick={layout.toggle}
    >
      <Icon name="layout" size="sm" />
    </Button>
  );
}
